
import { getPhoneDisplayCode } from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { ClientRegistrationController } from "@/app/ui/controllers/ClientRegistrationController";
import debounce from "lodash/debounce";
import { Options, prop, Vue } from "vue-class-component";
import ManagingCountryField, {
  ManagingCountryOption
} from "../form-sections/managing-country-field.vue";
import { DistrictData } from "@/domain/entities/Location";
import {
  TaxValidationConfig,
  validateTaxRegistration
} from "@/domain/client/validation/";

export type ClientBranchSectionProp = {
  companyName: string;
  companyAlias: string;
  address: string;
  district: string;
  email: string;
  phone: string;
  phoneDialCode?: string;
  taxNumber: string;
  isNeedSplitSo: boolean;
  managingCountry: ManagingCountryOption | null;
};

class Props {
  modelValue = prop<ClientBranchSectionProp>({
    required: true,
    type: Object
  });
  error = prop<any>({
    required: true
  });
  isMY = prop<boolean>({
    default: false
  });
  managingCountryOptions = prop<ManagingCountryOption[]>({
    default: () => [],
    required: false,
    type: Array
  });
  taxValidationConfig = prop<TaxValidationConfig | undefined>({
    default: undefined,
    required: false
  });
  isManagingCountryMY = prop<boolean>({
    default: false
  });
}

@Options({
  emits: ["update:modelValue", "update:error", "changeDistrict"],
  components: {
    ManagingCountryField
  },
  watch: {
    taxValidationConfig: {
      handler() {
        if (this.modelValue.taxNumber) {
          this.validateTaxNumber(this.modelValue.taxNumber);
        }
      },
      deep: true
    }
  }
})
export default class ClientBranchSection extends Vue.with(Props) {
  isOpenSplitSo = false;
  get openSplitSoList() {
    return [
      {
        name: this.$t("clientManagement.managementDetail.yes").toString(),
        value: true
      },
      {
        name: this.$t("clientManagement.managementDetail.no").toString(),
        value: false
      }
    ];
  }
  onOpenSelectSplitSo() {
    this.isOpenSplitSo = true;
  }

  onCloseSelectSplitSo() {
    this.isOpenSplitSo = false;
  }

  onSelectSplitSo(key: string, value: boolean) {
    this.onCloseSelectSplitSo();
    this.modelValue.isNeedSplitSo = value;
  }

  get defaultDialCode() {
    const countryCode =
      AccountController.accountData?.country_code ||
      AccountController.accountData?.countryCode;
    return getPhoneDisplayCode(countryCode);
  }

  get displayDialCode() {
    const d = this.modelValue.phoneDialCode;
    if (d === undefined || d === null || String(d).trim() === "") {
      return this.defaultDialCode;
    }
    return d;
  }

  get errorPhoneBranch() {
    return {
      error: this.error.phone,
      errorPhone: false,
      errorMandatory: false,
      errorPhoneCode: false,
      phoneCharMinimal: false,
      message: ""
    };
  }

  onSetPhone(v: string) {
    this.$emit("update:modelValue", { phone: v });
    this.validatePhone(v);
  }

  onSetPhoneDialCode(v: string) {
    this.$emit(
      "update:modelValue",
      Object.assign({}, this.modelValue as object, { phoneDialCode: v })
    );
  }

  onSetManagingCountry(v: ManagingCountryOption | null) {
    this.$emit(
      "update:modelValue",
      Object.assign({}, this.modelValue as object, { managingCountry: v })
    );
  }

  mounted(): void {
    this.fetchDistrictList("");
    if (!this.modelValue.phoneDialCode) {
      this.$emit("update:modelValue", {
        ...this.modelValue,
        phoneDialCode: this.defaultDialCode
      });
    }
  }

  // district list
  fetchDistrictList = debounce((search: string) => {
    ClientRegistrationController.getDistrictList({
      search: search
    });
  }, 250);

  get districtList() {
    return ClientRegistrationController.districtList;
  }

  get loadingDistrictList() {
    return ClientRegistrationController.loadingDistrictList;
  }

  validatePhone(value: string) {
    this.error.phone = !/^\d+$/.test(value) || value.length < 8;
  }

  formatNumber(value: string) {
    const newValue = value.replace(/[^0-9.-]/g, "");
    return newValue;
  }

  validateTaxNumber(value: string) {
    if (!value) {
      this.error.taxNumber = false;
      this.error.taxNumberMessage = "";
      return;
    }
    const isMY = this.isManagingCountryMY || this.isMY;
    const { valid, message } = validateTaxRegistration(
      value,
      this.taxValidationConfig,
      isMY,
      false
    );

    this.error.taxNumber = !valid;
    this.error.taxNumberMessage = !valid ? (message || "Masukan Nomor NPWP") : "";
  }

  validateEmail(value: string) {
    this.error.email = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    this.$emit("update:modelValue", {
      ...this.modelValue,
      email: value
    });
  }

  validateAlias(value: string) {
    this.error.alias = value.length < 3 || value.length > 100;
  }

  get phoneCodePrefix() {
    return this.isMY ? "+60" : "+62";
  }

  get taxNumberMaxLength() {
    // add dynamic max tax length based on config;
    const maxTaxNumberFallback = 16;
    if (
      !this.taxValidationConfig ||
      !this.taxValidationConfig.validationEnabled
    )
      return maxTaxNumberFallback;
    if (this.taxValidationConfig.maxLength !== 0)
      return this.taxValidationConfig.maxLength;
    return maxTaxNumberFallback;
  }

  formatTaxNumber(str: string): string {
    const isMY = this.isManagingCountryMY || this.isMY;
    const numericOnly =
      this.taxValidationConfig && this.taxValidationConfig.validationEnabled
        ? this.taxValidationConfig.numericOnly
        : !isMY;

    const formatted = str.replace(numericOnly ? /\D+/g : /[^a-zA-Z0-9]/g, "");

    return formatted.slice(0, this.taxNumberMaxLength);
  }

  get taxPlaceholder() {
    if (
      this.taxValidationConfig?.validationEnabled &&
      this.taxValidationConfig.minLength > 0
    ) {
      return `Masukkan ${this.taxValidationConfig.minLength} digit sesuai NPWP`;
    }
    return "Masukkan 16 digit sesuai NPWP";
  }

  onChangeDistrict(value: DistrictData) {
    this.$emit("changeDistrict", value);
  }
}
